import { Box, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { updateSEO } from "../utils/SEO";

// Extracted styles for better maintainability
const styles = {
  ccontainer: {
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#e6e4df",
    marginTop: 0,
    paddingTop: 0
  },
  imageContainer: {
    position: "relative",
    width: "100%",
    height: { xs: "70vh", sm: "89vh", md: "160vh", lg: '160vh' },
    backgroundColor: "#e6e4df",
    overflow: "hidden",
    marginBottom: { xs: "0", sm: "0", md: "0" },
    marginTop: { xs: "20px", sm: "30px", md: 0 }
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "bottom center",
    display: "block",
    transition: "opacity 0.3s ease-in-out",
  },
  textSection: {
    width: "100%",
    backgroundColor: "#e6e4df",
    color: "black",
    display: "flex",
    justifyContent: "center",
    marginTop: { xs: "-2rem", sm: "-3rem", md: "-4rem" },
    position: "relative",
    zIndex: 1
  },
  textContainer: {
    width: { xs: "90%", sm: "85%", md: "75%" },
    textAlign: "center",
    padding: { xs: "2rem", sm: "4rem", md: "6rem" },
  },
  text: {
    fontSize: { xs: "1rem", sm: "1.2rem", md: "1.5rem" },
    lineHeight: { xs: 1.6, md: 1.8 },
    letterSpacing: "0.01em",
    marginBottom: "2rem",
    fontFamily: '"Old Standard TT", serif',
    fontWeight: 100,
    fontStyle: "normal"
  },
  ctaButton: {
    marginTop: "2rem",
    padding: "1rem 2rem",
    backgroundColor: "#464d22",
    color: "white",
    fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.3rem" },
    fontWeight: 500,
    letterSpacing: "0.02em",
    borderRadius: "20px",
    fontFamily: '"Bebas Neue", sans-serif',
    minWidth: { xs: "200px", sm: "250px", md: "300px" },
    "&:hover": {
      backgroundColor: "#757a5c",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
    transition: "all 0.3s ease-in-out",
  },
};

const Home = () => {
  useEffect(() => {
    updateSEO({
      title: "Nicole Hahn Designs | Luxury Interior Design & Home Styling | Seattle, WA",
      description: "Nicole Hahn Designs offers luxury interior design services in Seattle, Bellevue, Tacoma, and surrounding areas. Specializing in custom new construction, remodels, and kitchen design with warm, neutral tones and natural materials.",
      url: "https://nicolehahndesigns.com/",
      image: "https://nicolehahndesigns.com/home1.jpg"
    });

    // Add enhanced structured data for AI search engines
    const businessScript = document.createElement('script');
    businessScript.type = 'application/ld+json';
    businessScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HomeAndConstructionBusiness",
      "name": "Nicole Hahn Designs",
      "alternateName": "NHD",
      "image": [
        "https://nicolehahndesigns.com/logo.jpg",
        "https://nicolehahndesigns.com/home1.jpg"
      ],
      "description": "Full service boutique interior design firm based in Lake Tapps, Washington, specializing in custom new construction builds, remodels, and kitchen and bath design. We create timeless designs using warm, neutral tones and quality natural materials.",
      "areaServed": [
        {
          "@type": "Place",
          "name": "Seattle, WA"
        },
        {
          "@type": "Place",
          "name": "Bellevue, WA"
        },
        {
          "@type": "Place",
          "name": "Tacoma, WA"
        },
        {
          "@type": "Place",
          "name": "King County, WA"
        },
        {
          "@type": "Place",
          "name": "Pierce County, WA"
        }
      ],
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "47.6062",
        "longitude": "-122.3321"
      },
      "url": "https://nicolehahndesigns.com",
      "email": "info@nicolehahndesigns.com",
      "priceRange": "$$$",
      "openingHours": [
        "Mo-Fr 09:00-17:00"
      ],
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "info@nicolehahndesigns.com",
          "availableLanguage": ["English"],
          "areaServed": ["US"]
        }
      ],
      "sameAs": [
        "https://www.instagram.com/nicolehahndesigns/"
      ],
      "founder": {
        "@type": "Person",
        "name": "Nicole Hahn",
        "jobTitle": "Owner/Principal Designer"
      },
      "knowsAbout": [
        "Interior Design",
        "Custom Home Design",
        "Kitchen Design",
        "Bathroom Design",
        "Home Remodeling",
        "Space Planning",
        "Color Consulting",
        "Furniture Selection",
        "Material Selection"
      ],
      "serviceType": "Interior Design Services"
    });
    document.head.appendChild(businessScript);

    // Add FAQ schema for AI search
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What interior design services does Nicole Hahn Designs offer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nicole Hahn Designs offers three main services: Full Service for new construction and remodels (complete design from floor plans to final installation), Design Day (detailed design plans for clients to implement themselves), and Consultations (2-hour in-home sessions for paint colors, material selections, space planning, and furniture selection)."
          }
        },
        {
          "@type": "Question",
          "name": "What areas does Nicole Hahn Designs serve?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nicole Hahn Designs serves Seattle, Bellevue, Tacoma, and surrounding areas in King County and Pierce County, Washington. The business is based in Lake Tapps, WA."
          }
        },
        {
          "@type": "Question",
          "name": "What is Nicole Hahn's design style?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nicole Hahn specializes in creating timeless designs using warm, neutral tones and quality natural materials. Her design philosophy emphasizes earthy, natural, and timeless aesthetics that create functional, calming spaces that reflect the people who occupy them. She incorporates antiques and reclaimed materials to add character and make spaces feel lived in."
          }
        },
        {
          "@type": "Question",
          "name": "What are Nicole Hahn's qualifications?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nicole Hahn holds an Associate Degree in Interior Design from FIDM and a Bachelor of Arts Degree in Interior Design from Bellevue College's CIDA-accredited design program. Prior to launching Nicole Hahn Designs, she worked for several design companies with experience in custom furnishings and multi-million dollar custom homes."
          }
        },
        {
          "@type": "Question",
          "name": "How do I contact Nicole Hahn Designs?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can contact Nicole Hahn Designs by email at info@nicolehahndesigns.com or by filling out the contact form on the website. The business is open Monday through Friday, 9 AM to 5 PM."
          }
        },
        {
          "@type": "Question",
          "name": "What is the price range for Nicole Hahn Designs services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nicole Hahn Designs is a luxury interior design firm with a price range of $$$. Project budgets typically range from $30,000 to $500,000+, with Design Day services starting around $10,000. Consultations are available for smaller projects."
          }
        },
        {
          "@type": "Question",
          "name": "Does Nicole Hahn Designs work on kitchen and bathroom remodels?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Nicole Hahn Designs specializes in kitchen and bath design as part of their full service offerings. They handle everything from floor plan design to selecting architectural materials, finishes, fixtures, and custom cabinetry design."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // Add WebPage schema
    const webpageScript = document.createElement('script');
    webpageScript.type = 'application/ld+json';
    webpageScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Nicole Hahn Designs | Luxury Interior Design",
      "description": "Luxury interior design services in Seattle, Bellevue, Tacoma, and surrounding areas",
      "url": "https://nicolehahndesigns.com/",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://nicolehahndesigns.com/"
          }
        ]
      }
    });
    document.head.appendChild(webpageScript);

    return () => {
      // Cleanup: remove scripts when component unmounts
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        const text = script.text;
        if (text.includes('"@type": "HomeAndConstructionBusiness"') ||
          text.includes('"@type": "FAQPage"') ||
          text.includes('"@type": "WebPage"')) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <Box
      component="main"
      role="main"
      aria-label="Home"
      sx={styles.container}
    >
      <Box sx={styles.imageContainer}>
        <Box
          component="img"
          src="/home1.jpg"
          alt="Luxury interior design living room with warm neutral tones and natural materials by Nicole Hahn Designs in Seattle, Washington"
          loading="eager"
          sx={{
            width: "100vw",
            height: { xs: "70vh", sm: "89vh", md: "160vh", lg: '160vh' },
            objectFit: "cover",
            objectPosition: { xs: "center 60%", sm: "center 60%", md: "center 60%", lg: "center 60%" },
            margin: 0,
            padding: 0,
            position: "absolute",
            bottom: 0,
            left: "50%",
            right: "50%",
            marginLeft: "-50vw",
            marginRight: "-50vw",
            maxWidth: "100vw"
          }}
        />
      </Box>

      {/* Text Section */}
      <Box
        sx={styles.textSection}
        component="section"
        aria-label="About Nicole Hahn Designs"
      >
        <Box sx={styles.textContainer}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              marginBottom: "2rem",
              fontWeight: { xs: 300, sm: 300, md: "bold" },
              fontFamily: '"Bebas Neue", sans-serif'
            }}
          >
            Welcome to Nicole Hahn Designs
          </Typography>

          <Typography
            variant="body1"
            component="p"
            sx={styles.text}
          >
            Nicole Hahn Designs is a full service boutique interior design firm based in Lake Tapps, WA that specializes in custom new construction builds, remodels, and kitchen and bath design. Our approach is to layer warm, neutral tones with quality, natural materials to create timeless designs that will be loved for years to come.
          </Typography>

          <Button
            variant="contained"
            component={Link}
            to="/contact"
            sx={styles.ctaButton}
          >
            Start Your Design Journey
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;